import { useEffect, useMemo } from 'react';
import {
  DEFAULT_ACCOUNT_CONFIG,
  useAccountConfig,
  useAccountConfigScope,
  useAccountConfigStatus,
} from '@course-authoring/shared-types';
import { createBlockPacing, type BlockPacing } from '../blockPacing';

/** Keep the owner above lesson rendering; stable identity preserves language/re-entry state. */
export function useBlockPacing(attemptKey: string): BlockPacing {
  const config = useAccountConfig();
  const status = useAccountConfigStatus();
  const accountScope = useAccountConfigScope();
  // Initial configuration selects the launch mode, but later same-scope config
  // refreshes must not replace the owner or change its resolved duration.
  const pacing = useMemo(() => createBlockPacing(
    status === 'loading'
      ? null
      : (status === 'error' ? DEFAULT_ACCOUNT_CONFIG.contentPacingDuration : config.contentPacingDuration) * 1000,
  ), [attemptKey, accountScope]);
  useEffect(() => {
    pacing.activate();
    pacing.reconcile();
    document.addEventListener('visibilitychange', pacing.reconcile);
    window.addEventListener('pageshow', pacing.reconcile);
    return () => {
      document.removeEventListener('visibilitychange', pacing.reconcile);
      window.removeEventListener('pageshow', pacing.reconcile);
      pacing.dispose();
    };
  }, [pacing]);
  useEffect(() => {
    if (pacing.getMode() !== 'unresolved' || status === 'loading') return;
    const duration = status === 'error'
      ? DEFAULT_ACCOUNT_CONFIG.contentPacingDuration
      : config.contentPacingDuration;
    pacing.resolve(duration * 1000);
  }, [pacing, status, config.contentPacingDuration]);
  return pacing;
}
